export const LOADER_VIEW_BOX = '0 0 231 216';

export const LOGO_TOP_BAR =
  'M229.858 71.305L217.026 9.70852C215.159 0.702359 203.12 -2.75078 188.981 2.4056L40.4025 56.5359C35.876 58.1924 31.8396 64.9354 32.9362 71.0484L40.3791 112.743C41.4991 118.949 47.4954 123.032 54.005 121.796L209.256 92.5372C222.299 90.0873 231.772 80.5212 229.858 71.305Z';

export const LOGO_BOTTOM_BAR =
  'M167.071 141.276C165.345 132.76 154.612 127.44 143.553 129.306L8.62367 152.125C2.83733 153.105 -0.872458 158.635 0.177483 164.538L7.31708 204.669C8.39035 210.642 13.99 215.495 19.9864 215.472L160.772 215.332C172.368 215.332 180.697 208.239 178.924 199.536L167.048 141.252L167.071 141.276Z';

export const CHECK_LONG_STROKE =
  'M106.86 166.99L196.86 70.99A19 19 0 0 0 169.14 45.01L79.14 141.01A19 19 0 0 0 106.86 166.99Z';

export const CHECK_SHORT_STROKE =
  'M35.56 123.44L79.56 167.44A19 19 0 0 0 106.44 140.56L62.44 96.56A19 19 0 0 0 35.56 123.44Z';

const SAMPLE_COUNT = 96;
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

type Point = readonly [number, number];
type PointPair = readonly [from: Point[], to: Point[]];

export interface LoaderMorph {
  top: PointPair;
  bottom: PointPair;
}

/**
 * Samples each logo bar and its checkmark stroke into equally long point lists, so the
 * shapes can be interpolated point by point. Returns null where the browser cannot measure
 * SVG paths (jsdom); callers then show the checkmark without morphing.
 */
export function createLoaderMorph(document: Document): LoaderMorph | null {
  const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
  const path = document.createElementNS(SVG_NAMESPACE, 'path');

  if (typeof path.getTotalLength !== 'function') {
    return null;
  }

  svg.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden');
  svg.appendChild(path);
  document.body.appendChild(svg);

  const top = samplePath(path, LOGO_TOP_BAR);
  const bottom = samplePath(path, LOGO_BOTTOM_BAR);
  const morph: LoaderMorph = {
    top: [top, alignPoints(top, samplePath(path, CHECK_LONG_STROKE))],
    bottom: [bottom, alignPoints(bottom, samplePath(path, CHECK_SHORT_STROKE))],
  };

  svg.remove();

  return morph;
}

export function interpolatePath([from, to]: PointPair, progress: number): string {
  const commands = from.map(([fromX, fromY], pointIndex) => {
    const [toX, toY] = to[pointIndex];
    const pointX = (fromX + (toX - fromX) * progress).toFixed(2);
    const pointY = (fromY + (toY - fromY) * progress).toFixed(2);

    return `${pointIndex === 0 ? 'M' : 'L'}${pointX} ${pointY}`;
  });

  return `${commands.join('')}Z`;
}

export function easeInOutCubic(progress: number): number {
  return progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
}

function samplePath(path: SVGPathElement, pathData: string): Point[] {
  path.setAttribute('d', pathData);
  const length = path.getTotalLength();

  return Array.from({ length: SAMPLE_COUNT }, (_, sampleIndex): Point => {
    const point = path.getPointAtLength((length * sampleIndex) / SAMPLE_COUNT);

    return [point.x, point.y];
  });
}

function signedArea(points: Point[]): number {
  return points.reduce((area, [currentX, currentY], pointIndex) => {
    const [nextX, nextY] = points[(pointIndex + 1) % points.length];

    return area + currentX * nextY - nextX * currentY;
  }, 0);
}

/**
 * Rotates (and if needed reverses) `target` so each of its points lies closest to the
 * matching point of `source`; without it the morph twists through itself.
 */
function alignPoints(source: Point[], target: Point[]): Point[] {
  const sameWinding = Math.sign(signedArea(source)) === Math.sign(signedArea(target));
  const oriented = sameWinding ? target : target.toReversed();

  let bestOffset = 0;
  let bestDistance = Infinity;

  for (let offset = 0; offset < oriented.length; offset++) {
    const distance = source.reduce((sum, [sourceX, sourceY], pointIndex) => {
      const [targetX, targetY] = oriented[(pointIndex + offset) % oriented.length];

      return sum + (sourceX - targetX) ** 2 + (sourceY - targetY) ** 2;
    }, 0);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestOffset = offset;
    }
  }

  return [...oriented.slice(bestOffset), ...oriented.slice(0, bestOffset)];
}
